import './ShortcutsGuide.scss';
import { IconDefinition, library } from '@fortawesome/fontawesome-svg-core';
import { faBackspace, faKeyboard } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TranslationKey, useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { Fragment, ReactElement } from 'react';
import { detect } from 'un-detector';
import { S_Modal } from '../../../../design/modals/modal/S_Modal';

library.add(faKeyboard, faBackspace);

const KeyboardShortcut = ({ children }: { children: string | IconDefinition }): ReactElement => (
  <div className='keyboard-shortcut'>
    <>{typeof children === 'string' ? children : <FontAwesomeIcon icon={children} />}</>
  </div>
);

const ShortcutListItem = ({ left, right }: { left: string; right: ReactElement }): ReactElement => (
  <div className='shortcut-item'>
    <div className='shortcut-title'>{left}</div>
    <div className='shortcut-description'>{right}</div>
  </div>
);
const ShortcutTitleListItem = ({ title }: { title: string }): ReactElement => (
  <div className='shortcut-section-title'>
    <div className='shortcut-title'>{title}</div>
  </div>
);
interface IProps {
  isOpen: boolean;
  globalShortcuts: { label: TranslationKey; keys: string[] }[];
  create?: boolean;
  review?: boolean;
  correct?: boolean;
  edit?: boolean;
  isFreeAccess?: boolean;
  toggle: () => void;
}

export const ShortcutsGuide = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();

  const { isOpen, toggle } = props;

  const detected = detect(window.navigator.userAgent);

  const globalShortcuts: { label: TranslationKey; keys: string[] }[] = [...props.globalShortcuts, { label: 'show_shortcut_help', keys: ['?'] }];
  return (
    <S_Modal
      isOpen={isOpen}
      toggle={toggle}
      className='shortcuts-modal'
      headerText={t('shortcuts')}
      buttons={
        <>
          <Button
            variant='outlined'
            onClick={props.toggle}
          >
            {t('close_window')}
          </Button>
        </>
      }
    >
      <div className='shortcuts-list'>
        {globalShortcuts.length !== 0 && (
          <>
            <ShortcutTitleListItem title={t('shortcuts_global')} />
            {globalShortcuts.map((shortcut) => (
              <ShortcutListItem
                key={shortcut.label}
                left={t(shortcut.label)}
                right={
                  <>
                    {shortcut.keys.map((key, index) => (
                      <Fragment key={key}>
                        {index !== 0 && <>&nbsp;+&nbsp; </>}
                        <KeyboardShortcut>{key}</KeyboardShortcut>
                      </Fragment>
                    ))}
                  </>
                }
              />
            ))}
          </>
        )}
        {props.isFreeAccess && (
          <>
            <ShortcutTitleListItem title={t('shortcuts_navigation')} />
            <ShortcutListItem
              left={t('previous')}
              right={<KeyboardShortcut>{'←'}</KeyboardShortcut>}
            />
            <ShortcutListItem
              left={t('next')}
              right={<KeyboardShortcut>{'→'}</KeyboardShortcut>}
            />
          </>
        )}
        {(props.create || props.correct) && (
          <>
            <ShortcutTitleListItem title={t('shortcuts_annotation')} />
            <ShortcutListItem
              left={t('select_tool')}
              right={<KeyboardShortcut>{'͠ˎ'}</KeyboardShortcut>}
            />
            <ShortcutListItem
              left={t('annotation_tools')}
              right={
                <>
                  <KeyboardShortcut>{'1'}</KeyboardShortcut>
                  &nbsp;...&nbsp;
                  <KeyboardShortcut>{'0'}</KeyboardShortcut>
                </>
              }
            />
            <ShortcutListItem
              left={t('copy_marking')}
              right={
                <>
                  <KeyboardShortcut>{'ctrl'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'c'}</KeyboardShortcut>
                </>
              }
            />
            <ShortcutListItem
              left={t('paste_marking')}
              right={
                <>
                  <KeyboardShortcut>{'ctrl'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'v'}</KeyboardShortcut>
                </>
              }
            />
            <ShortcutListItem
              left={t('undo_last_action')}
              right={
                <>
                  <KeyboardShortcut>{'ctrl'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'z'}</KeyboardShortcut>
                </>
              }
            />
            <ShortcutListItem
              left={t('redo_last_action')}
              right={
                <>
                  <KeyboardShortcut>{'ctrl'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'y'}</KeyboardShortcut>
                </>
              }
            />
            {detected.is.mac ? (
              <ShortcutListItem
                left={t('delete_marking')}
                right={
                  <>
                    <KeyboardShortcut>{'fn'}</KeyboardShortcut>
                    &nbsp;+&nbsp;
                    <KeyboardShortcut>{'{faBackspace}'}</KeyboardShortcut>
                  </>
                }
              />
            ) : (
              <ShortcutListItem
                left={t('delete_marking')}
                right={<KeyboardShortcut>{'del'}</KeyboardShortcut>}
              />
            )}
          </>
        )}
        {(props.correct || (props.create && !props.edit)) && (
          <>
            <ShortcutListItem
              left={t('submit_annotation')}
              right={
                <>
                  <KeyboardShortcut>{'ctrl'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'shift'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'enter'}</KeyboardShortcut>
                </>
              }
            />
          </>
        )}
        {props.correct && (
          <>
            <ShortcutListItem
              left={t('reject')}
              right={
                <>
                  <KeyboardShortcut>{'ctrl'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'shift'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'backspace'}</KeyboardShortcut>
                </>
              }
            />
          </>
        )}
        {props.edit && (
          <>
            <ShortcutListItem
              left={t('save_annotation')}
              right={
                <>
                  <KeyboardShortcut>{'ctrl'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'enter'}</KeyboardShortcut>
                </>
              }
            />
          </>
        )}
        {(props.create || props.correct) && (
          <>
            <ShortcutTitleListItem title={t('shortcuts_annotation_question')} />
            <ShortcutListItem
              left={t('shortcuts_annotation_question_navigation')}
              right={
                <>
                  <KeyboardShortcut>{'tab'}</KeyboardShortcut>
                  &nbsp;/&nbsp;
                  <KeyboardShortcut>{'shift'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'tab'}</KeyboardShortcut>
                </>
              }
            />
            <ShortcutListItem
              left={t('shortcuts_annotation_question_checkbox_up_down')}
              right={
                <>
                  <KeyboardShortcut>{'tab'}</KeyboardShortcut>
                  &nbsp;/&nbsp;
                  <KeyboardShortcut>{'shift'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'tab'}</KeyboardShortcut>
                </>
              }
            />
            <ShortcutListItem
              left={t('shortcuts_annotation_question_checkbox_selection')}
              right={
                <>
                  <KeyboardShortcut>{'space'}</KeyboardShortcut>
                </>
              }
            />
            <ShortcutListItem
              left={t('shortcuts_annotation_question_radiobutton')}
              right={
                <>
                  <KeyboardShortcut>{'↑'}</KeyboardShortcut>
                  &nbsp;/&nbsp;
                  <KeyboardShortcut>{'↓'}</KeyboardShortcut>
                </>
              }
            />
            <ShortcutListItem
              left={t('shortcuts_annotation_question_dropdown')}
              right={
                <>
                  <KeyboardShortcut>{'↑'}</KeyboardShortcut>
                  &nbsp;/&nbsp;
                  <KeyboardShortcut>{'↓'}</KeyboardShortcut>
                </>
              }
            />
          </>
        )}
        {props.review && (
          <>
            <ShortcutTitleListItem title={t('shortcuts_review')} />
            <ShortcutListItem
              left={t('accept')}
              right={
                <>
                  <KeyboardShortcut>{'ctrl'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'shift'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'enter'}</KeyboardShortcut>
                </>
              }
            />
            <ShortcutListItem
              left={t('reject')}
              right={
                <>
                  <KeyboardShortcut>{'ctrl'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'shift'}</KeyboardShortcut>
                  &nbsp;+&nbsp;
                  <KeyboardShortcut>{'backspace'}</KeyboardShortcut>
                </>
              }
            />
            <ShortcutListItem
              left={t('fix')}
              right={<KeyboardShortcut>{'e'}</KeyboardShortcut>}
            />
          </>
        )}
      </div>
    </S_Modal>
  );
});
